import React, {useState, Fragment, useEffect} from 'react';
import {Modal, Button, Tooltip, Form, Radio, Input, notification, Space} from 'antd';
import {CheckOutlined, ForkOutlined} from '@ant-design/icons';
import semver from 'semver';

function CommitDetailsModal({visible, version, versions, onCommit, setVisible}) {
    const [form] = Form.useForm();
    const [versionType, setVersionType] = useState('patch');
    const [prerelease, setPreRelease] = useState('beta');
    const [saving, setSaving] = useState(false);

    const handleCancel = () => setVisible(false);
    const handleCommit = async () => {
        try {
            setSaving(true);
            const values = await form.validateFields();
            onCommit &&
                (await onCommit({
                    ...values,
                    username: window.username,
                }));
            setVisible(false);
            notification.success({title: `Version ${values.version} was successfully committed`, placement: 'bottomRight'});
        } catch (err) {
            console.error(err);
            notification.error({title: err.message, placement: 'bottomRight'});
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        form.resetFields();
        const values = {type: versionType};
        if (version === 'latest') {
            if (versions.length > 0) {
                values.previous = versions[0];
            } else {
                values.previous = '0.0.0';
            }
        } else {
            values.previous = version;
        }
        form.setFieldsValue(values);
    }, [version]);

    useEffect(() => {
        const previous = form.getFieldValue('previous');
        if (versionType === 'pre') {
            form.setFieldsValue({version: semver.inc(previous, 'prerelease', prerelease)});
        } else {
            form.setFieldsValue({version: semver.inc(previous, versionType)});
        }
    }, [versionType, prerelease]);

    return (
        <Modal
            open={visible}
            title={
                <span>
                    <ForkOutlined id="commit" />
                    Commit changes
                </span>
            }
            footer={null}
            onCancel={handleCancel}>
            <Form form={form} layout="horizontal" onFinish={handleCommit}>
                <Form.Item label="Current Version: " name="previous">
                    <Input />
                </Form.Item>
                <Form.Item label="New Version: " name="version">
                    <Input />
                </Form.Item>
                {versionType && (
                    <Form.Item label="Type (Semantic)" name="type" rules={[{required: true, message: 'Please select version type'}]}>
                        <Radio.Group buttonStyle="solid" onChange={(e) => setVersionType(e.target.value)}>
                            <Radio.Button type="primary" value="major">
                                Major
                            </Radio.Button>
                            <Radio.Button value="minor">Minor</Radio.Button>
                            <Radio.Button value="patch">Patch</Radio.Button>
                            <Radio.Button value="pre">Pre-Release</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                )}
                {versionType === 'pre' && (
                    <Form.Item label="Pre-Release keyword (beta, alpha): " name="pre">
                        <Input onChange={(e) => setPreRelease(e.target.value)} />
                    </Form.Item>
                )}
                <Form.Item label="Comments: " name="comment" rules={[{required: true, message: 'Please input your comments'}]}>
                    <Input.TextArea rows={4} />
                </Form.Item>
                <Form.Item>
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Space>
                            <Button htmlType="button" className="commitCancel" onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button type="primary" className="commitButton" loading={saving} htmlType="submit">
                                Commit Changes
                            </Button>
                        </Space>
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
}

export function CommitDetailsDialog({version = '1.0.0', onCommit, versions = []}) {
    const [visible, setVisible] = useState();

    return (
        <Fragment>
            {!visible && (
                <Tooltip title="Commit changes" placement="bottom">
                    <Button
                        className="commitButton"
                        type="primary"
                        style={{marginLeft: 5, display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                        icon={<CheckOutlined />}
                        onClick={() => setVisible(true)}
                    />
                </Tooltip>
            )}
            {visible && <CommitDetailsModal visible={visible} setVisible={setVisible} version={version} onCommit={onCommit} versions={versions} />}
        </Fragment>
    );
}
